import type { Book, BookProgress, Chapter } from '../types';

/** Sum of every track duration we currently know. Unknown tracks count as 0,
  * so this firms up as files get loaded rather than being wrong up front. */
export function knownTotalDuration(book: Book): number {
   return book.tracks.reduce((sum, t) => sum + (t.durationSec ?? 0), 0);
}

export function allDurationsKnown(book: Book): boolean {
   return book.tracks.every((t) => typeof t.durationSec === 'number' && t.durationSec > 0);
}

/** Position measured from the start of the book rather than the current track. */
export function absolutePosition(book: Book, trackIndex: number, positionSec: number): number {
   let sum = 0;
   for (let i = 0; i < trackIndex && i < book.tracks.length; i++) {
     sum += book.tracks[i]?.durationSec ?? 0;
   }
   return sum + positionSec;
}

/** 0..1 through the whole book, or 0 when durations are not yet known. */
export function bookRatio(book: Book, entry: BookProgress | undefined): number {
   if (!entry) return 0;
   if (entry.finished) return 1;
   const total = knownTotalDuration(book);
   if (total <= 0) return 0;
   return Math.min(1, absolutePosition(book, entry.trackIndex, entry.positionSec) / total);
}

export function remainingSeconds(book: Book, entry: BookProgress | undefined): number {
   const total = knownTotalDuration(book);
   if (total <= 0) return 0;
   if (!entry) return total;
   if (entry.finished) return 0;
   return Math.max(0, total - absolutePosition(book, entry.trackIndex, entry.positionSec));
}

/** The chapter list a book should show. Multi-file books fall back to one
  * entry per file, which is what the file names mean in practice. */
export function effectiveChapters(book: Book, parsed: Chapter[]): Chapter[] {
   if (parsed.length > 0) return parsed;
   return book.tracks.map((t, i) => ({ title: t.name, trackIndex: i, startSec: 0 }));
}

/** Duration of a chapter, given the chapter after it and the track lengths. */
export function chapterDuration(book: Book, chapters: Chapter[], index: number): number {
   const chapter = chapters[index];
   if (!chapter) return 0;
    const next = chapters[index + 1];
    const trackDuration = book.tracks[chapter.trackIndex]?.durationSec ?? 0;

    if (next && next.trackIndex === chapter.trackIndex) return next.startSec - chapter.startSec;
    return Math.max(0, trackDuration - chapter.startSec);
}
